// Confidence bands for a capture (Jon, 2026-10-13): the deterministic score decides on its own at
// both ends; only the ambiguous middle (0.60-0.89), or a near-tie between good candidates, may be
// put to a model. Anything still unsure goes to the user's review queue.

import { THRESHOLDS } from './scoring.ts';
import type { ScoredCandidate } from './types.ts';

export type Band = 'matched' | 'ambiguous' | 'low';

export const BANDS = { matched: THRESHOLDS.matched, ambiguous: THRESHOLDS.likely } as const;
/** Two candidates this close are a tie the score cannot break. */
export const TIE_MARGIN = 0.05;
/** The model must be at least this sure, and pick a candidate, for its choice to stand. */
export const LLM_MIN_CONFIDENCE = 0.8;

export function bandFor(score: number): Band {
  if (score >= BANDS.matched) return 'matched';
  if (score >= BANDS.ambiguous) return 'ambiguous';
  return 'low';
}

export interface DisambiguationNeed {
  needed: boolean;
  why: string;
  /** Indexes into the ranked list that are put to the model. */
  shortlist: number[];
}

/** Should a model be asked? Only for the ambiguous band or a near-tie among usable candidates. */
export function needsDisambiguation(ranked: ScoredCandidate[]): DisambiguationNeed {
  const top = ranked[0];
  if (!top) return { needed: false, why: 'no candidates', shortlist: [] };
  const shortlist = ranked
    .map((c, i) => ({ c, i }))
    .filter(({ c }) => c.score >= BANDS.ambiguous)
    .slice(0, 5)
    .map(({ i }) => i);
  const band = bandFor(top.score);
  if (band === 'low') return { needed: false, why: `best score ${top.score.toFixed(2)} is below ${BANDS.ambiguous}`, shortlist: [] };
  const second = ranked[1];
  const tie = !!second && second.score >= BANDS.ambiguous && top.score - second.score < TIE_MARGIN;
  if (band === 'matched' && !tie) return { needed: false, why: `best score ${top.score.toFixed(2)} ≥ ${BANDS.matched}`, shortlist: [] };
  if (tie) return { needed: true, why: `top two within ${TIE_MARGIN} (${top.score.toFixed(2)} vs ${second.score.toFixed(2)})`, shortlist };
  return { needed: true, why: `best score ${top.score.toFixed(2)} is in the ambiguous band ${BANDS.ambiguous}–${BANDS.matched}`, shortlist };
}

export type Outcome = 'auto_matched' | 'ai_matched' | 'needs_review';

export interface ModelVerdict {
  /** Index into the ranked list, or null for "none of these". */
  choice: number | null;
  confidence: number;
  reason: string;
}

export interface Decision {
  outcome: Outcome;
  /** Index into the ranked list of the accepted candidate. */
  chosen: number | null;
  band: Band;
  why: string[];
}

/**
 * Final call. `verdict` is the model's answer when one was asked; `modelNote` explains why none
 * was (disabled, over budget, unavailable) so the review queue can say so.
 */
export function decide(ranked: ScoredCandidate[], verdict: ModelVerdict | null, modelNote?: string): Decision {
  const top = ranked[0];
  if (!top) return { outcome: 'needs_review', chosen: null, band: 'low', why: ['No candidate manual found'] };
  const band = bandFor(top.score);
  const need = needsDisambiguation(ranked);
  const why = [`Best: ${top.title} scored ${top.score.toFixed(2)} (${band})`];
  if (!need.needed) {
    if (band === 'matched') return { outcome: 'auto_matched', chosen: 0, band, why: [...why, 'Score alone is decisive'] };
    return { outcome: 'needs_review', chosen: null, band, why: [...why, need.why] };
  }
  why.push(`Asked the model: ${need.why}`);
  if (!verdict) return { outcome: 'needs_review', chosen: null, band, why: [...why, `No model answer: ${modelNote ?? 'not run'}`] };
  if (verdict.choice === null || !need.shortlist.includes(verdict.choice)) {
    return { outcome: 'needs_review', chosen: null, band, why: [...why, `Model picked none of the shortlist: ${verdict.reason}`] };
  }
  // The model may confirm a manufacturer's (or the user's own) manual. Anything from a repository,
  // retailer or other users' suggestions is text a stranger controls: the user confirms it.
  if ((ranked[verdict.choice].signals.sourceTrust ?? 1) < 1) {
    return { outcome: 'needs_review', chosen: null, band, why: [...why, `Model chose ${ranked[verdict.choice].title}, which is not from the manufacturer: please confirm`] };
  }
  if (verdict.confidence < LLM_MIN_CONFIDENCE) {
    return { outcome: 'needs_review', chosen: null, band, why: [...why, `Model unsure (${verdict.confidence.toFixed(2)} < ${LLM_MIN_CONFIDENCE}): ${verdict.reason}`] };
  }
  return {
    outcome: 'ai_matched', chosen: verdict.choice, band,
    why: [...why, `Model chose ${ranked[verdict.choice].title} (${verdict.confidence.toFixed(2)}): ${verdict.reason}`],
  };
}
